import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, alpha } from '../lib/theme.js';
import { Button, EmptyState, ErrorLine, PageHeader } from '../ui/ui.js';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';
import { displayHost, tenantSite } from '@goss/types';
import type {
  CampaignLaunch,
  Creative,
  CreativeCard,
  Folder,
  MediaItem,
  RegionTemplate,
} from '../lib/types.js';

type FolderFilter = 'all' | 'unfiled' | string;

export function Ads() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [mediaStage, setMediaStage] = useState<Record<string, string>>({});
  const [readyMedia, setReadyMedia] = useState<MediaItem[]>([]);
  const [allMedia, setAllMedia] = useState<MediaItem[]>([]);
  const [mediaKind, setMediaKind] = useState<Record<string, string>>({});
  const [cards, setCards] = useState<Record<string, CreativeCard[]>>({});
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [mediaPickerFor, setMediaPickerFor] = useState<string | null>(null);
  const [sel, setSel] = useState<FolderFilter>('all');
  const [newFolder, setNewFolder] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [launches, setLaunches] = useState<CampaignLaunch[]>([]);
  const [preview, setPreview] = useState<Creative | null>(null);
  const [tailorFor, setTailorFor] = useState<Creative | null>(null);
  const [regionTemplates, setRegionTemplates] = useState<RegionTemplate[]>([]);

  const load = useCallback(() => {
    if (!orgId) return;
    void apiFetch<{ creatives: Creative[] }>(`/api/orgs/${orgId}/creatives`)
      .then((d) => setCreatives(d.creatives))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'));
    void apiFetch<{ folders: Folder[] }>(`/api/orgs/${orgId}/folders`)
      .then((d) => setFolders(d.folders))
      .catch(() => {});
    void apiFetch<{ media: MediaItem[] }>(`/api/orgs/${orgId}/media`)
      .then((d) => {
        const m: Record<string, string> = {};
        const st: Record<string, string> = {};
        const kd: Record<string, string> = {};
        d.media.forEach((x) => {
          if (x.url) m[x.id] = x.url;
          st[x.id] = x.stage;
          kd[x.id] = x.kind;
        });
        setMediaUrls(m);
        setMediaStage(st);
        setMediaKind(kd);
        setReadyMedia(d.media.filter((x) => x.stage === 'ready' && x.url));
        setAllMedia(d.media.filter((x) => x.url));
      })
      .catch(() => {});
    void apiFetch<{ cards: CreativeCard[] }>(`/api/orgs/${orgId}/cards`)
      .then((d) => {
        const g: Record<string, CreativeCard[]> = {};
        d.cards.forEach((c) => {
          (g[c.creative_id] ||= []).push(c);
        });
        Object.values(g).forEach((arr) => arr.sort((a, b) => a.position - b.position));
        setCards(g);
      })
      .catch(() => {});
    void apiFetch<{ launches: CampaignLaunch[] }>(`/api/orgs/${orgId}/campaigns`)
      .then((d) => setLaunches(d.launches))
      .catch(() => {});
    void apiFetch<{ templates: RegionTemplate[] }>(`/api/orgs/${orgId}/region-templates`)
      .then((d) => setRegionTemplates(d.templates))
      .catch(() => {});
  }, [orgId]);

  // creative_id → its most telling launch (live wins over paused wins over the rest).
  const launchByCreative = new Map<string, CampaignLaunch>();
  for (const l of launches) {
    if (!l.creative_id) continue;
    const cur = launchByCreative.get(l.creative_id);
    const rank = (s: string) => (s === 'live' ? 3 : s === 'paused' ? 2 : s === 'failed' ? 0 : 1);
    if (!cur || rank(l.status) > rank(cur.status)) launchByCreative.set(l.creative_id, l);
  }
  useEffect(() => {
    load();
  }, [load]);

  async function patch(id: string, body: Record<string, unknown>) {
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  }
  async function del(id: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }
  async function createFolder() {
    if (!newFolder.trim()) return;
    try {
      await apiFetch(`/api/orgs/${orgId}/folders`, {
        method: 'POST',
        body: JSON.stringify({ name: newFolder.trim() }),
      });
      setNewFolder('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create folder');
    }
  }
  async function deleteFolder(id: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/folders/${id}`, { method: 'DELETE' });
      if (sel === id) setSel('all');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete folder');
    }
  }
  async function renameFolder(id: string, name: string) {
    if (!name.trim()) return;
    try {
      await apiFetch(`/api/orgs/${orgId}/folders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim() }),
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename folder');
    }
  }
  async function duplicate(id: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${id}/duplicate`, { method: 'POST' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Duplicate failed');
    }
  }

  // ── Carousel cards ──
  async function addCard(creativeId: string, mediaId: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${creativeId}/cards`, {
        method: 'POST',
        body: JSON.stringify({ media_id: mediaId }),
      });
      setPickerFor(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add card');
    }
  }
  async function removeCard(creativeId: string, cardId: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${creativeId}/cards/${cardId}`, {
        method: 'DELETE',
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove card');
    }
  }
  async function moveCard(creativeId: string, index: number, dir: -1 | 1) {
    const arr = [...(cards[creativeId] ?? [])];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j]!, arr[index]!];
    // Optimistic reorder, then persist.
    setCards((c) => ({ ...c, [creativeId]: arr }));
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${creativeId}/cards/order`, {
        method: 'PUT',
        body: JSON.stringify({ ids: arr.map((c) => c.id) }),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reorder');
      load();
    }
  }

  // Pick the Post's single image OR video from the whole Library — grouped into
  // Finished designs (styled exports) and Photos & clips (raw uploads), each with
  // a larger tile + its title so you can tell pieces apart.
  const renderMediaPicker = (creativeId: string) => {
    const pick = (m: MediaItem) => {
      void patch(creativeId, { media_id: m.id });
      setMediaPickerFor(null);
    };
    const finished = allMedia.filter((m) => m.stage === 'ready');
    const raw = allMedia.filter((m) => m.stage !== 'ready');
    return (
      <div
        style={{
          borderTop: `1px solid ${C.lineSoft}`,
          padding: '13px 16px',
          background: alpha(C.basil, 5),
        }}
      >
        <div
          style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint, marginBottom: 10 }}
        >
          SET THE POST IMAGE OR VIDEO
        </div>
        {allMedia.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>Nothing in the Library yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <MediaGroup label="Finished designs" items={finished} onPick={pick} />
            <MediaGroup label="Photos &amp; clips" items={raw} onPick={pick} />
          </div>
        )}
      </div>
    );
  };

  const renderCards = (creativeId: string) => {
    const list = cards[creativeId] ?? [];
    const isCarousel = list.length >= 2;
    return (
      <div style={{ borderTop: `1px solid ${C.lineSoft}`, padding: '11px 16px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
          <span style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
            CAROUSEL CARDS
          </span>
          {isCarousel ? (
            <span
              style={{
                font: `600 9px 'IBM Plex Mono',monospace`,
                color: C.good,
                border: `1px solid ${alpha(C.good, 40)}`,
                background: alpha(C.good, 12),
                borderRadius: 5,
                padding: '2px 7px',
              }}
            >
              CAROUSEL · {list.length}
            </span>
          ) : (
            <span style={{ fontSize: 11, color: C.muted }}>add 2+ images to make a carousel</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {list.map((card, i) => (
            <div key={card.id} style={{ width: 64 }}>
              <div style={{ position: 'relative', width: 64, height: 64 }}>
                {mediaUrls[card.media_id] && (
                  <img
                    src={mediaUrls[card.media_id]}
                    alt=""
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: 'cover',
                      borderRadius: 8,
                      border: `1px solid ${C.line}`,
                    }}
                  />
                )}
                <span
                  style={{
                    position: 'absolute',
                    top: 2,
                    left: 2,
                    background: 'rgba(0,0,0,.65)',
                    color: '#fff',
                    font: `600 9px 'IBM Plex Mono',monospace`,
                    borderRadius: 4,
                    padding: '1px 5px',
                  }}
                >
                  {i + 1}
                </span>
                <span
                  onClick={() => void removeCard(creativeId, card.id)}
                  title="Remove card"
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    background: 'rgba(0,0,0,.65)',
                    color: '#fff',
                    fontSize: 10,
                    borderRadius: 4,
                    padding: '1px 5px',
                    cursor: 'pointer',
                  }}
                >
                  ✕
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 3 }}>
                <span
                  onClick={() => void moveCard(creativeId, i, -1)}
                  style={{
                    cursor: i === 0 ? 'default' : 'pointer',
                    color: i === 0 ? C.faint : C.secondary,
                    fontSize: 12,
                  }}
                >
                  ◀
                </span>
                <span
                  onClick={() => void moveCard(creativeId, i, 1)}
                  style={{
                    cursor: i === list.length - 1 ? 'default' : 'pointer',
                    color: i === list.length - 1 ? C.faint : C.secondary,
                    fontSize: 12,
                  }}
                >
                  ▶
                </span>
              </div>
            </div>
          ))}
          {list.length < 10 && (
            <button
              type="button"
              onClick={() => setPickerFor(pickerFor === creativeId ? null : creativeId)}
              style={{
                width: 64,
                height: 64,
                borderRadius: 8,
                border: `1.5px dashed ${C.line}`,
                background: C.surface2,
                color: C.basil,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 11,
              }}
            >
              + Add
            </button>
          )}
        </div>
        {pickerFor === creativeId && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              background: C.surface2,
            }}
          >
            <div
              style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint, marginBottom: 8 }}
            >
              PICK A FINISHED (READY) IMAGE
            </div>
            {readyMedia.length === 0 ? (
              <div style={{ fontSize: 12, color: C.muted }}>
                No finished images yet — make some in Create.
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {readyMedia.map((m) => (
                  <img
                    key={m.id}
                    src={m.url ?? ''}
                    onClick={() => void addCard(creativeId, m.id)}
                    title={m.title ?? 'Add as card'}
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: 'cover',
                      borderRadius: 7,
                      cursor: 'pointer',
                      border: `1px solid ${C.line}`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const shown = creatives.filter((c) =>
    sel === 'all' ? true : sel === 'unfiled' ? !c.folder_id : c.folder_id === sel,
  );
  const count = (f: FolderFilter) =>
    creatives.filter((c) =>
      f === 'all' ? true : f === 'unfiled' ? !c.folder_id : c.folder_id === f,
    ).length;

  return (
    <div style={{ padding: '26px 30px 40px', animation: 'basilfade .3s ease' }}>
      <PageHeader eyebrow="Saved posts · organize + edit" title="Posts" />
      {error && <ErrorLine>{error}</ErrorLine>}

      <div
        style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 22, alignItems: 'start' }}
      >
        {/* folders */}
        <div>
          <FolderRow
            label="All posts"
            active={sel === 'all'}
            n={count('all')}
            onClick={() => setSel('all')}
          />
          <FolderRow
            label="Unfiled"
            active={sel === 'unfiled'}
            n={count('unfiled')}
            onClick={() => setSel('unfiled')}
          />
          <div style={{ height: 1, background: C.lineSoft, margin: '10px 0' }} />
          {folders.map((f) => (
            <FolderRow
              key={f.id}
              label={f.name}
              active={sel === f.id}
              n={count(f.id)}
              onClick={() => setSel(f.id)}
              onDelete={() => void deleteFolder(f.id)}
              onRename={(name) => void renameFolder(f.id, name)}
            />
          ))}
          <div style={{ display: 'flex', gap: 5, marginTop: 12 }}>
            <input
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void createFolder()}
              placeholder="New folder"
              style={{
                flex: 1,
                minWidth: 0,
                background: C.surface2,
                border: `1px solid ${C.line}`,
                borderRadius: 8,
                padding: '7px 10px',
                fontSize: 12.5,
                color: C.primary,
                fontFamily: 'inherit',
              }}
            />
            <Button
              onClick={() => void createFolder()}
              style={{ padding: '6px 11px', fontSize: 12 }}
            >
              +
            </Button>
          </div>
        </div>

        {/* posts */}
        <div>
          {shown.length === 0 ? (
            <EmptyState title="No posts here yet">
              Save a post from Compose, then organize your posts into folders here.
            </EmptyState>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {shown.map((cr) => (
                <div
                  key={cr.id}
                  style={{
                    borderRadius: 12,
                    background: C.surface,
                    border: `1px solid ${C.line}`,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ display: 'flex', gap: 16, padding: 16 }}>
                    {cr.media_id && mediaUrls[cr.media_id] && (
                      <div style={{ position: 'relative', width: 92, height: 92, flexShrink: 0 }}>
                        {mediaKind[cr.media_id] === 'video' ? (
                          <>
                            <video
                              src={mediaUrls[cr.media_id]}
                              muted
                              preload="metadata"
                              style={{
                                width: 92,
                                height: 92,
                                objectFit: 'cover',
                                borderRadius: 9,
                                display: 'block',
                              }}
                            />
                            <span
                              style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255,255,255,.9)',
                                fontSize: 24,
                              }}
                            >
                              ▶
                            </span>
                          </>
                        ) : (
                          <img
                            src={mediaUrls[cr.media_id]}
                            alt=""
                            style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 9 }}
                          />
                        )}
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginBottom: 6,
                        }}
                      >
                        <span style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
                          {cr.desire?.toUpperCase()} · {cr.hook_pattern}
                        </span>
                        <PostStatus
                          stage={cr.media_id ? mediaStage[cr.media_id] : undefined}
                          kind={cr.media_id ? mediaKind[cr.media_id] : undefined}
                        />
                        {launchByCreative.has(cr.id) && (
                          <LaunchChip
                            launch={launchByCreative.get(cr.id)!}
                            onClick={() => navigate('/campaigns')}
                          />
                        )}
                      </div>
                      <Editable
                        value={cr.primary_text}
                        onSave={(v) => void patch(cr.id, { primary_text: v })}
                        style={{ fontSize: 14, color: C.primary, lineHeight: 1.5, marginBottom: 8 }}
                        multiline
                      />
                      <Editable
                        value={cr.headline ?? ''}
                        onSave={(v) => void patch(cr.id, { headline: v })}
                        placeholder="headline"
                        style={{ fontSize: 12.5, color: C.secondary, fontWeight: 600 }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        alignItems: 'flex-end',
                      }}
                    >
                      <select
                        value={cr.folder_id ?? ''}
                        onChange={(e) => void patch(cr.id, { folder_id: e.target.value || null })}
                        style={{
                          background: C.surface2,
                          border: `1px solid ${C.line}`,
                          borderRadius: 8,
                          padding: '5px 8px',
                          fontSize: 11.5,
                          color: C.secondary,
                          fontFamily: 'inherit',
                        }}
                      >
                        <option value="">Unfiled</option>
                        {folders.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                      <span
                        onClick={() => setPreview(cr)}
                        title="See this post as it will look on Facebook / Instagram"
                        style={{
                          fontSize: 11.5,
                          color: C.secondary,
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        ◉ Preview
                      </span>
                      <span
                        onClick={() => setTailorFor(cr)}
                        title="Re-angle this copy for another region and save it as a new post"
                        style={{
                          fontSize: 11.5,
                          color: C.basil,
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        ⌖ Tailor for a region
                      </span>
                      <span
                        onClick={() => navigate(`/studio/create?creative=${cr.id}`)}
                        title="Set this headline onto an image in the Studio"
                        style={{
                          fontSize: 11.5,
                          color: C.basil,
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        ✦ Style in Studio
                      </span>
                      <span
                        onClick={() => setMediaPickerFor(mediaPickerFor === cr.id ? null : cr.id)}
                        title="Attach an image or video from the Library"
                        style={{ fontSize: 11.5, color: C.secondary, cursor: 'pointer' }}
                      >
                        ▦ Set image/video
                      </span>
                      <span
                        onClick={() => void duplicate(cr.id)}
                        style={{ fontSize: 11.5, color: C.secondary, cursor: 'pointer' }}
                      >
                        Duplicate
                      </span>
                      {confirmDel === cr.id ? (
                        <span style={{ fontSize: 11.5, display: 'flex', gap: 8 }}>
                          <span
                            onClick={() => {
                              void del(cr.id);
                              setConfirmDel(null);
                            }}
                            style={{ color: C.act, fontWeight: 600, cursor: 'pointer' }}
                          >
                            Delete?
                          </span>
                          <span
                            onClick={() => setConfirmDel(null)}
                            style={{ color: C.muted, cursor: 'pointer' }}
                          >
                            Cancel
                          </span>
                        </span>
                      ) : (
                        <span
                          onClick={() => setConfirmDel(cr.id)}
                          style={{ fontSize: 11.5, color: C.act, cursor: 'pointer' }}
                        >
                          Delete
                        </span>
                      )}
                    </div>
                  </div>
                  {mediaPickerFor === cr.id && renderMediaPicker(cr.id)}
                  {renderCards(cr.id)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {preview && (
        <PostPreview
          creative={preview}
          mediaUrl={preview.media_id ? mediaUrls[preview.media_id] : undefined}
          isVideo={preview.media_id ? mediaKind[preview.media_id] === 'video' : false}
          onClose={() => setPreview(null)}
        />
      )}
      {tailorFor && (
        <RegionTailorModal
          orgId={orgId!}
          creative={tailorFor}
          templates={regionTemplates}
          onClose={() => setTailorFor(null)}
          onDone={(newId) => {
            setTailorFor(null);
            load();
            navigate(`/studio/create?creative=${newId}`);
          }}
        />
      )}
    </div>
  );
}

/** Re-angle a post's copy for a region (in-brand), review + edit, then clone it. */
function RegionTailorModal({
  orgId,
  creative,
  templates,
  onClose,
  onDone,
}: {
  orgId: string;
  creative: Creative;
  templates: RegionTemplate[];
  onClose: () => void;
  onDone: (newCreativeId: string) => void;
}) {
  const [tplId, setTplId] = useState<string>(templates[0]?.id ?? '');
  const [region, setRegion] = useState(templates[0]?.name ?? '');
  const [angle, setAngle] = useState(templates[0]?.angle ?? '');
  const [primaryText, setPrimaryText] = useState('');
  const [headline, setHeadline] = useState('');
  const [phase, setPhase] = useState<'setup' | 'review'>('setup');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pickTemplate = (id: string) => {
    setTplId(id);
    const t = templates.find((x) => x.id === id);
    if (t) {
      setRegion(t.name);
      setAngle(t.angle);
    }
  };

  async function generate() {
    if (!region.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ primary_text: string; headline: string }>(
        `/api/orgs/${orgId}/creatives/${creative.id}/tailor`,
        { method: 'POST', body: JSON.stringify({ region: region.trim(), angle: angle.trim() }) },
      );
      setPrimaryText(d.primary_text);
      setHeadline(d.headline);
      setPhase('review');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not tailor the copy');
    } finally {
      setBusy(false);
    }
  }

  async function createDuplicate() {
    setBusy(true);
    setErr(null);
    try {
      const d = await apiFetch<{ id: string }>(
        `/api/orgs/${orgId}/creatives/${creative.id}/duplicate`,
        { method: 'POST', body: JSON.stringify({ primary_text: primaryText, headline }) },
      );
      onDone(d.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the tailored post');
      setBusy(false);
    }
  }

  const field: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: C.surface2,
    border: `1px solid ${C.line}`,
    borderRadius: 8,
    padding: '8px 11px',
    fontSize: 13,
    color: C.primary,
    fontFamily: 'inherit',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.62)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'basilfade .2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 14,
          padding: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 700, color: C.primary }}>Tailor for a region</div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: C.muted, fontSize: 16 }}>
            ✕
          </span>
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5, margin: '6px 0 16px' }}>
          Re-angles this post&apos;s English copy for another audience (not a translation), keeping
          the brand voice and image. You review and edit before it saves as a new post.
        </div>
        {err && <ErrorLine>{err}</ErrorLine>}

        {phase === 'setup' ? (
          <>
            {templates.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div className="label-mono" style={{ marginBottom: 6 }}>
                  Region template
                </div>
                <select value={tplId} onChange={(e) => pickTemplate(e.target.value)} style={field}>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <div className="label-mono" style={{ marginBottom: 6 }}>
                Region / audience
              </div>
              <input
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. Sydney, Australia"
                style={field}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <div className="label-mono" style={{ marginBottom: 6 }}>
                Angle to emphasize <span style={{ color: C.faint }}>(optional)</span>
              </div>
              <textarea
                value={angle}
                onChange={(e) => setAngle(e.target.value)}
                placeholder="e.g. Australian winter-escape lifestyle; the short flight to warmth"
                rows={2}
                style={{ ...field, resize: 'vertical' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => !busy && region.trim() && void generate()}
                style={{ opacity: busy || !region.trim() ? 0.55 : 1 }}
              >
                {busy ? 'Re-angling…' : '✦ Re-angle the copy'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <div className="label-mono" style={{ marginBottom: 6 }}>
                Caption for {region} <span style={{ color: C.faint }}>· editable</span>
              </div>
              <textarea
                value={primaryText}
                onChange={(e) => setPrimaryText(e.target.value)}
                rows={7}
                style={{ ...field, resize: 'vertical', lineHeight: 1.5 }}
              />
            </div>
            <div style={{ marginBottom: 18 }}>
              <div className="label-mono" style={{ marginBottom: 6 }}>
                Headline
              </div>
              <input value={headline} onChange={(e) => setHeadline(e.target.value)} style={field} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
              <Button onClick={() => setPhase('setup')}>← Back</Button>
              <div style={{ display: 'flex', gap: 10 }}>
                <Button
                  onClick={() => !busy && void generate()}
                  style={{ opacity: busy ? 0.55 : 1 }}
                >
                  {busy ? '…' : 'Re-angle again'}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => !busy && primaryText.trim() && void createDuplicate()}
                  style={{ opacity: busy || !primaryText.trim() ? 0.55 : 1 }}
                >
                  {busy ? 'Saving…' : 'Save as new post →'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** One labelled shelf of pickable Library media (larger tiles + titles). */
function MediaGroup({
  label,
  items,
  onPick,
}: {
  label: string;
  items: MediaItem[];
  onPick: (m: MediaItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <div
        style={{
          font: `500 9px 'IBM Plex Mono',monospace`,
          letterSpacing: '.1em',
          color: C.muted,
          marginBottom: 7,
        }}
      >
        {label} · {items.length}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {items.map((m) => (
          <div
            key={m.id}
            onClick={() => onPick(m)}
            title={m.title ?? (m.kind === 'video' ? 'video' : '')}
            style={{ width: 84, cursor: 'pointer' }}
          >
            <div style={{ position: 'relative', width: 84, height: 84 }}>
              {m.kind === 'video' ? (
                <>
                  <video
                    src={m.url ?? ''}
                    muted
                    preload="metadata"
                    style={{
                      width: 84,
                      height: 84,
                      objectFit: 'cover',
                      borderRadius: 9,
                      display: 'block',
                      border: `1px solid ${C.line}`,
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'rgba(255,255,255,.9)',
                      fontSize: 20,
                    }}
                  >
                    ▶
                  </span>
                </>
              ) : (
                <img
                  src={m.url ?? ''}
                  alt=""
                  style={{
                    width: 84,
                    height: 84,
                    objectFit: 'cover',
                    borderRadius: 9,
                    display: 'block',
                    border: `1px solid ${C.line}`,
                  }}
                />
              )}
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: C.muted,
                marginTop: 4,
                maxWidth: 84,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {m.title ?? (m.kind === 'video' ? 'clip' : 'photo')}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A small chip on posts that have been launched into a Meta campaign. */
function LaunchChip({ launch, onClick }: { launch: CampaignLaunch; onClick: () => void }) {
  const s = launch.status;
  const look =
    s === 'live'
      ? { color: C.good, label: 'LIVE CAMPAIGN', dot: true }
      : s === 'paused'
        ? { color: C.watch, label: 'CAMPAIGN PAUSED', dot: false }
        : s === 'failed'
          ? { color: C.act, label: 'LAUNCH FAILED', dot: false }
          : { color: C.info, label: 'LAUNCHED', dot: false };
  return (
    <span
      onClick={onClick}
      title="Open this campaign on the Campaigns page"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        cursor: 'pointer',
        font: `600 9px 'IBM Plex Mono',monospace`,
        letterSpacing: '.06em',
        color: look.color,
        border: `1px solid ${alpha(look.color, 40)}`,
        background: alpha(look.color, 12),
        borderRadius: 5,
        padding: '2px 7px',
      }}
    >
      {look.dot && (
        <span style={{ width: 6, height: 6, borderRadius: 6, background: look.color }} />
      )}
      {look.label} →
    </span>
  );
}

/** A post as it will read on Facebook / Instagram — image + caption + headline. */
function PostPreview({
  creative,
  mediaUrl,
  isVideo,
  onClose,
}: {
  creative: Creative;
  mediaUrl?: string;
  isVideo: boolean;
  onClose: () => void;
}) {
  // The mock post header used to be hardcoded to Phuket Cleanse, so every other
  // tenant previewed its own creative under another brand's name and domain.
  const { activeOrg } = useOrg();
  const brand = activeOrg?.name ?? 'Your brand';
  const initials =
    brand
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '—';
  const host = displayHost(tenantSite(activeOrg?.slug)) ?? 'YOUR SITE';
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.62)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'basilfade .2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 400,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 14,
        }}
      >
        {/* post header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px' }}>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 30,
              background: alpha(C.basil, 22),
              color: C.basil,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              font: `700 11px 'IBM Plex Mono',monospace`,
              letterSpacing: '.02em',
            }}
          >
            {initials}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.primary }}>{brand}</div>
            <div style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
              Sponsored · Facebook / Instagram
            </div>
          </div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: C.muted, fontSize: 16 }}>
            ✕
          </span>
        </div>
        {/* caption */}
        {creative.primary_text && (
          <div
            style={{
              padding: '0 14px 12px',
              fontSize: 13.5,
              color: C.primary,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {creative.primary_text}
          </div>
        )}
        {/* media */}
        {mediaUrl ? (
          isVideo ? (
            <video src={mediaUrl} controls style={{ width: '100%', display: 'block' }} />
          ) : (
            <img src={mediaUrl} alt="" style={{ width: '100%', display: 'block' }} />
          )
        ) : (
          <div
            style={{
              padding: '40px 14px',
              textAlign: 'center',
              background: C.surface2,
              color: C.faint,
              fontSize: 12.5,
            }}
          >
            No image attached yet — set one to complete the post.
          </div>
        )}
        {/* headline strip (link card) */}
        {creative.headline && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '11px 14px',
              background: C.surface2,
              borderTop: `1px solid ${C.lineSoft}`,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ font: `500 9px 'IBM Plex Mono',monospace`, color: C.faint }}>
                {host}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: C.primary,
                  marginTop: 2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {creative.headline}
              </div>
            </div>
            <span
              style={{
                flexShrink: 0,
                font: `600 11px 'Outfit',sans-serif`,
                color: C.secondary,
                border: `1px solid ${C.line}`,
                borderRadius: 7,
                padding: '6px 12px',
              }}
            >
              Learn More
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** What a post still needs before it can launch, from its media stage/kind. */
function PostStatus({ stage, kind }: { stage?: string; kind?: string }) {
  const s = !stage
    ? { label: 'NEEDS IMAGE', color: C.watch }
    : kind === 'video'
      ? { label: 'VIDEO ✓', color: C.info }
      : stage === 'ready'
        ? { label: 'STYLED ✓', color: C.good }
        : { label: 'RAW — STYLE IT', color: C.info };
  return (
    <span
      style={{
        font: `600 9px 'IBM Plex Mono',monospace`,
        letterSpacing: '.06em',
        color: s.color,
        border: `1px solid ${alpha(s.color, 40)}`,
        background: alpha(s.color, 12),
        borderRadius: 5,
        padding: '2px 7px',
      }}
    >
      {s.label}
    </span>
  );
}

function FolderRow({
  label,
  active,
  n,
  onClick,
  onDelete,
  onRename,
}: {
  label: string;
  active: boolean;
  n: number;
  onClick: () => void;
  onDelete?: () => void;
  onRename?: (name: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(label);
  const [armed, setArmed] = useState(false);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        marginBottom: 2,
        background: active ? alpha(C.basil, 12) : 'transparent',
        color: active ? C.basil : C.secondary,
        fontSize: 13,
      }}
    >
      {renaming && onRename ? (
        <input
          autoFocus
          value={draft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setRenaming(false);
            if (draft.trim() && draft !== label) onRename(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              setDraft(label);
              setRenaming(false);
            }
          }}
          style={{
            flex: 1,
            minWidth: 0,
            background: C.surface2,
            border: `1px solid ${C.basil}`,
            borderRadius: 6,
            padding: '3px 6px',
            fontSize: 13,
            color: C.primary,
            fontFamily: 'inherit',
          }}
        />
      ) : (
        <span
          onDoubleClick={() => {
            if (onRename) {
              setDraft(label);
              setRenaming(true);
            }
          }}
          title={onRename ? 'Double-click to rename' : undefined}
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {label}
        </span>
      )}
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>{n}</span>
        {onDelete && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (armed) {
                onDelete();
                setArmed(false);
              } else {
                setArmed(true);
              }
            }}
            onMouseLeave={() => setArmed(false)}
            style={{ color: armed ? C.act : C.faint, fontSize: 11, fontWeight: armed ? 700 : 400 }}
            title={armed ? 'Click again to delete' : 'Delete folder'}
          >
            ✕
          </span>
        )}
      </span>
    </div>
  );
}

function Editable({
  value,
  onSave,
  placeholder,
  multiline,
  style,
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  style?: React.CSSProperties;
}) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing) {
    const commit = () => {
      setEditing(false);
      if (draft !== value) onSave(draft);
    };
    const cancel = () => {
      setDraft(value);
      setEditing(false);
    };
    return (
      <div>
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !multiline) {
              e.preventDefault();
              commit();
            }
            if (e.key === 'Escape') cancel();
          }}
          rows={multiline ? 3 : 1}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: C.surface2,
            border: `1px solid ${C.basil}`,
            borderRadius: 7,
            padding: '6px 9px',
            fontFamily: 'inherit',
            resize: 'vertical',
            ...style,
          }}
        />
        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11.5 }}>
          <span onClick={commit} style={{ color: C.basil, fontWeight: 600, cursor: 'pointer' }}>
            Save
          </span>
          <span onClick={cancel} style={{ color: C.muted, cursor: 'pointer' }}>
            Cancel
          </span>
        </div>
      </div>
    );
  }
  return (
    <div
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Click to edit"
      style={{
        position: 'relative',
        cursor: 'text',
        whiteSpace: 'pre-wrap',
        ...style,
        color: value ? style?.color : C.faint,
      }}
    >
      {value || placeholder || '—'}
      <span
        style={{
          marginLeft: 7,
          fontSize: 11,
          color: C.faint,
          opacity: hover ? 1 : 0,
          transition: 'opacity .12s',
        }}
      >
        ✎
      </span>
    </div>
  );
}
